import React, { useContext } from "react";
import { Accordion, Card, Button, useAccordionButton } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import type { CompanyGroup, CompanyLocation } from "../types/companyGroup";
import type { Company } from "../types/auth";
import { AccordionContext } from "react-bootstrap";

interface CustomToggleProps {
  children: React.ReactNode;
  eventKey: string;
}

const CustomToggle: React.FC<CustomToggleProps> = ({ children, eventKey }) => {
  const { activeEventKey } = useContext(AccordionContext);
  const decoratedOnClick = useAccordionButton(eventKey);
  const isCurrentEventKey = activeEventKey === eventKey;

  return (
    <div
      className={`accordion-button custom-toggle ${!isCurrentEventKey ? "collapsed" : ""}`}
      onClick={decoratedOnClick}
      role="button"
      style={{ cursor: "pointer" }}
    >
      {children}
    </div>
  );
};

interface HierarchyTreeProps {
  groups: CompanyGroup[];
  locations: CompanyLocation[];
  allCompanies: Company[];
  onCreateGroup: () => void;
  onCreateLocation: (groupId: number) => void;
  onEditGroup: (group: CompanyGroup) => void;
  onEditLocation: (location: CompanyLocation) => void;
  onEditCompany: (company: Company) => void;
  onAssignCompany: (location: CompanyLocation) => void;
  onDeleteGroup: (groupId: number) => void;
  onDeleteLocation: (locationId: number, groupId: number) => void;
  onUnassignCompany: (company: Company) => void;
}

const HierarchyTree: React.FC<HierarchyTreeProps> = ({
  groups,
  locations,
  allCompanies,
  onCreateGroup,
  onCreateLocation,
  onEditGroup,
  onEditLocation,
  onEditCompany,
  onAssignCompany,
  onDeleteGroup,
  onDeleteLocation,
  onUnassignCompany,
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'hierarchyTree' });
  const { t: tc } = useTranslation('common');
  const getLocationsForGroup = (groupId: number) =>
    locations.filter((l) => l.company_group_id === groupId);

  const getCompaniesForLocation = (locationId: number) =>
    allCompanies.filter((c) => c.company_location_id === locationId);

  return (
    <>
      <div className="d-flex gap-2 mb-4 an-hierarchy-toolbar">
        <Button
          variant="primary"
          size="sm"
          onClick={onCreateGroup}
        >
          <i className="bi bi-plus-lg"></i> {t('addGroup')}
        </Button>
      </div>

      <Accordion defaultActiveKey="0" className="hierarchy-tree hierarchy-tree-modern">
        {groups.map((group, idx) => {
          const locationsForGroup = getLocationsForGroup(group.id);
          return (
            <Accordion.Item
              eventKey={idx.toString()}
              key={group.id}
              className="mb-3 hierarchy-group-card"
            >
              <div className="group-node-header d-flex align-items-center justify-content-between border-bottom pe-3">
                <CustomToggle eventKey={idx.toString()}>
                  <div className="d-flex align-items-center">
                    <div className="node-indicator primary me-3"></div>
                    <div>
                      <h6 className="mb-0 fw-bold">{group.name} {t('groupSuffix')}</h6>
                    </div>
                  </div>
                </CustomToggle>
                <div className="d-flex gap-2">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-1 text-primary text-decoration-none"
                    onClick={() => onEditGroup(group)}
                  >
                    <i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span>
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-1 text-danger text-decoration-none"
                    onClick={() => onDeleteGroup(group.id)}
                  >
                    <i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span>
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => onCreateLocation(group.id)}
                  >
                    <i className="bi bi-plus-lg"></i> {t('addLocation')}
                  </Button>
                </div>
              </div>
              <Accordion.Body>
                {locationsForGroup.map((loc) => (
                  <Card
                    key={loc.id}
                    className="mb-3 hierarchy-location-card"
                  >
                    <Card.Body className="py-2 px-3">
                      <div className="d-flex align-items-center justify-content-between mb-2 hierarchy-location-header">
                        <div className="d-flex align-items-center">
                          <div className="node-indicator info me-3"></div>
                          <div>
                            <span className="fw-bold d-block">
                              {loc.name} {t('locationSuffix')}
                            </span>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-success"
                            title={t('assignCompany')}
                            onClick={() => onAssignCompany(loc)}
                          >
                            <i className="bi bi-plus-lg"></i>
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-primary text-decoration-none"
                            onClick={() => onEditLocation(loc)}
                          >
                            <i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span>
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-danger text-decoration-none"
                            onClick={() => onDeleteLocation(loc.id, group.id)}
                            disabled={locationsForGroup.length <= 1}
                          >
                            <i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span>
                          </Button>
                        </div>
                      </div>
                      <div className="ps-4 hierarchy-company-list">
                        {getCompaniesForLocation(loc.id).map((comp) => {
                          const companiesForLocation = getCompaniesForLocation(loc.id);
                          return (
                            <div
                              key={comp.id}
                              className="d-flex align-items-center justify-content-between mb-2 hierarchy-company-node"
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  className="node-indicator success me-2"
                                  style={{ height: "16px", width: "3px" }}
                                ></div>
                                <span className="small text-muted">
                                  {comp.name} ({comp.company_id})
                                </span>
                              </div>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-1 text-primary opacity-75"
                                  title={t('editCompany')}
                                  onClick={() => onEditCompany(comp)}
                                >
                                  <i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span>
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-1 text-danger text-decoration-none opacity-75"
                                  title={t('unassignCompany')}
                                  onClick={() => onUnassignCompany(comp)}
                                  disabled={companiesForLocation.length <= 1}
                                >
                                  <i className="bi bi-x-lg"></i>
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                        {getCompaniesForLocation(loc.id).length === 0 && (
                          <span className="small text-muted fst-italic">
                            {t('noCompaniesAssigned')}
                          </span>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>

      <style>{`
        .hierarchy-tree-modern .accordion-button:not(.collapsed) {
          background-color: rgb(var(--an-primary-rgb) / 0.06);
          color: var(--an-primary);
          box-shadow: none;
        }
        .hierarchy-tree-modern .custom-toggle {
          flex-grow: 1;
          padding: 1rem 1.25rem;
          border: none;
          background: var(--an-transparent);
          text-align: left;
          display: flex;
          align-items: center;
          min-width: 0;
        }
        .hierarchy-tree-modern .custom-toggle:after {
          flex-shrink: 0;
          width: 0.65rem;
          height: 0.65rem;
          margin-left: auto;
          content: "";
          border: solid var(--an-text-secondary);
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
          transition: transform var(--an-transition), border-color var(--an-transition);
        }
        .hierarchy-tree-modern .custom-toggle:not(.collapsed):after {
          border-color: var(--an-primary);
          transform: rotate(-135deg);
        }
        .hierarchy-tree-modern .node-indicator {
          width: 4px;
          min-width: 4px;
          height: 24px;
          border-radius: 2px;
        }
        .hierarchy-tree-modern .node-indicator.primary { background-color: var(--an-primary); }
        .hierarchy-tree-modern .node-indicator.info { background-color: var(--an-info); }
        .hierarchy-tree-modern .node-indicator.success { background-color: var(--an-success); }
        .hierarchy-tree-modern .btn-link {
          border-radius: var(--an-radius-sm);
        }
      `}</style>
    </>
  );
};

export default HierarchyTree;